import { Button, Flex, Modal, Spin, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import {
  CalendarDays,
  CheckCircle2,
  ExternalLink,
  MapPin,
  Video,
} from "lucide-react";
import { useNavigate } from "react-router";
import { useGetAssessmentInterviewQuery } from "../../../app/features/recruitment/recruitment.api";
import type { CustomModalProps } from "../../../components/CustomModal";
import useAuth from "../../../hooks/useAuth";
import { PATHS } from "../../../routes/paths";

const { Text } = Typography;

type Props = CustomModalProps & { job_application_id?: string };

const InterviewDetails = ({ open, handleClose, job_application_id }: Props) => {
  const { verifiedInfo } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useGetAssessmentInterviewQuery(
    {
      applicant_id: verifiedInfo?.id as string,
      job_application_id: job_application_id as string,
    },
    { skip: !open || !verifiedInfo?.id || !job_application_id },
  );

  const interview = data?.data?.interview;

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={480}
      className="interview-details-modal"
    >
      {isLoading ? (
        <Flex align="center" justify="center" className="h-48">
          <Spin size="large" />
        </Flex>
      ) : interview ? (
        <div className="pt-2">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-600 border border-green-200 uppercase tracking-wider">
                <span className="size-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                Interview Scheduled
              </span>
            </div>
            <h2 className="text-xl font-bold text-theme-gray-800 mt-3">
              Your Interview Details
            </h2>
            <Text className="text-theme-gray-500 text-sm">
              Please review the information below and be prepared on time.
            </Text>
          </div>

          {/* Info Cards */}
          <div className="space-y-3">
            {/* Date */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-theme-gray-98 border border-theme-gray-90">
              <div className="size-10 rounded-lg bg-theme-primary/10 flex items-center justify-center shrink-0">
                <CalendarDays className="size-5 text-theme-primary" />
              </div>
              <div>
                <Text className="text-xs font-semibold text-theme-gray-500 uppercase tracking-wider block">
                  Date
                </Text>
                <Text className="text-base font-semibold text-theme-gray-800">
                  {formatDate(interview.interview_date)} |{" "}
                  {formatTime(interview.interview_time)}
                </Text>
              </div>
            </div>

            {/* Method */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-theme-gray-98 border border-theme-gray-90">
              <div className="size-10 rounded-lg bg-theme-primary/10 flex items-center justify-center shrink-0">
                <Video className="size-5 text-theme-primary" />
              </div>
              <div>
                <Text className="text-xs font-semibold text-theme-gray-500 uppercase tracking-wider block">
                  Method
                </Text>
                <Text className="text-base font-semibold text-theme-gray-800 capitalize">
                  {interview.interview_method}
                </Text>
              </div>
            </div>

            {/* Location (onsite) */}
            {interview.location && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-theme-gray-98 border border-theme-gray-90">
                <div className="size-10 rounded-lg bg-theme-primary/10 flex items-center justify-center shrink-0">
                  <MapPin className="size-5 text-theme-primary" />
                </div>
                <div>
                  <Text className="text-xs font-semibold text-theme-gray-500 uppercase tracking-wider block">
                    Location
                  </Text>
                  <Text className="text-base font-semibold text-theme-gray-800">
                    {interview.location}
                  </Text>
                </div>
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="mt-3">
            {interview.is_submitted_ai_interview ? (
              <div
                className="relative overflow-hidden rounded-xl p-4"
                style={{
                  background:
                    "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)",
                  border: "1px solid #6ee7b7",
                }}
              >
                {/* Decorative circles */}
                <div
                  className="absolute -top-4 -right-4 size-16 rounded-full opacity-20"
                  style={{ background: "#10b981" }}
                />
                <div
                  className="absolute -bottom-2 -left-2 size-10 rounded-full opacity-15"
                  style={{ background: "#10b981" }}
                />

                <div className="relative flex items-center gap-3">
                  <div
                    className="flex items-center justify-center size-11 rounded-full shrink-0"
                    style={{
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
                    }}
                  >
                    <CheckCircle2 className="size-6 text-white" />
                  </div>
                  <div>
                    <Text
                      className="text-base font-bold block"
                      style={{ color: "#065f46" }}
                    >
                      Interview Completed
                    </Text>
                    <Text className="text-xs" style={{ color: "#047857" }}>
                      Your AI interview has been successfully submitted.
                    </Text>
                  </div>
                </div>
              </div>
            ) : (
              interview.interview_method === "online" && (() => {
                const isAiInterview = interview.interview_type === "ai";
                const isBeforeScheduled = dayjs(
                  `${interview.interview_date} ${interview.interview_time}`,
                ).isAfter(dayjs().add(5, "minute"));

                // AI interviews are locked until the scheduled window opens.
                // Manual online interviews can be joined at any time.
                const isDisabled = isAiInterview && isBeforeScheduled;

                const tooltipTitle =
                  isDisabled
                    ? `The interview is scheduled for ${formatDate(interview.interview_date)} at ${formatTime(interview.interview_time)}`
                    : "";

                const handleClick = () => {
                  if (isAiInterview) {
                    navigate(PATHS.jobPortal.interview(job_application_id as string));
                  } else if (interview.meeting_link) {
                    window.open(
                      interview.meeting_link as string,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }
                };

                return (
                  <Tooltip title={tooltipTitle} placement="top">
                    <Button
                      block
                      size="large"
                      type="primary"
                      icon={<ExternalLink className="size-4" />}
                      onClick={handleClick}
                      className="btn-primary"
                      disabled={isDisabled}
                    >
                      Take Interview
                    </Button>
                  </Tooltip>
                );
              })()
            )}
          </div>
        </div>
      ) : (
        <Flex align="center" justify="center" className="h-48 flex-col gap-2">
          <Text className="text-theme-gray-500">
            No interview details available.
          </Text>
        </Flex>
      )}
    </Modal>
  );
};

export default InterviewDetails;
